import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTenant } from '../../contexts/TenantContext';
import { API_ORIGIN, certificateAPI, contentAPI } from '../../services/api';
import {
  MAX_DOCUMENT_UPLOAD_BYTES,
  MAX_CERTIFICATE_DOCUMENTS,
  documentFileValidationError,
  isAllowedDocumentFile,
  normalizePhoneInput,
  isValidPhone,
  requiredMessage,
  phoneMessage,
  formatMarathiNumber,
  toMarathiDigits,
  toEnglishDigits,
} from '../../utils/validation';

import {
  normalizeCustomFormFields,
  certificateInputType,
  resolveCertificateValues,
  isCertificateFieldVisible,
  isCertificateFieldRequired,
} from '../../utils/certificateFields';
import { downloadDocument } from '../../utils/documentDownload';
import DocumentPreviewTable from '../../components/DocumentPreviewTable';
import { loadRazorpay } from '../../utils/razorpay';

// Age Calculation Helper on Target Marriage Date
export const calculateAgeOnDate = (dob, targetDate) => {
  if (!dob || !targetDate) return null;
  const birth = new Date(dob);
  const target = new Date(targetDate);
  if (isNaN(birth.getTime()) || isNaN(target.getTime())) return null;
  let age = target.getFullYear() - birth.getFullYear();
  const monthDiff = target.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && target.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

// ── Styles ────────────────────────────────────────────────────────────
const labelStyle = { display: 'block', fontWeight: 600, marginBottom: 5, color: '#374151', fontSize: '0.88rem' };
const fieldStyle = {
  width: '100%', padding: '10px 14px', border: '1.5px solid #d1d5db', borderRadius: 8,
  fontSize: '0.95rem', boxSizing: 'border-box', fontFamily: 'inherit', transition: 'border-color 0.2s',
  outline: 'none', background: '#fff',
};
const fieldFocusColor = '#e67e22';

const stripRequiredMark = label => String(label || '').replace(/\s*\*$/, '');

const normalizeDocumentRequirements = value => {
  let raw = value;
  if (typeof value === 'string') {
    try {
      raw = JSON.parse(value);
    } catch {
      raw = value.split(/\r?\n|,/);
    }
  }
  if (!Array.isArray(raw)) raw = [];

  return raw.map(item => {
    if (typeof item === 'string') return { name: item.trim(), code: item.trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''), document_role: '', is_required: true };
    if (item && typeof item === 'object') {
      return {
        name: String(item.name || item.document_name || item.label || '').trim(),
        code: String(item.code || item.document_code || item.name || item.document_name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, ''),
        document_role: String(item.document_role || item.role || '').trim(),
        is_required: (item.is_required ?? item.required ?? true) !== false,
      };
    }
    return { name: '', is_required: true };
  }).filter(item => item.name);
};

const parseBilingualValue = val => {
  if (!val) return { mr: '', en: '' };
  const str = String(val);
  if (str.includes(' / ')) {
    const idx = str.indexOf(' / ');
    return { mr: str.slice(0, idx).trim(), en: str.slice(idx + 3).trim() };
  }
  if (str.includes('/')) {
    const idx = str.indexOf('/');
    return { mr: str.slice(0, idx).trim(), en: str.slice(idx + 1).trim() };
  }
  if (/[\u0900-\u097F]/.test(str)) {
    return { mr: str.trim(), en: '' };
  }
  return { mr: '', en: str.trim() };
};

function BilingualField({ label, required, type = 'text', value, onChange, placeholder, error, readOnly, helperText }) {
  const [focusedMr, setFocusedMr] = useState(false);
  const [focusedEn, setFocusedEn] = useState(false);
  const isMultiline = type === 'textarea';
  const mrStyle = { ...fieldStyle, borderColor: error ? '#dc2626' : focusedMr ? fieldFocusColor : '#d1d5db', resize: isMultiline ? 'vertical' : undefined };
  const enStyle = { ...fieldStyle, borderColor: error ? '#dc2626' : focusedEn ? fieldFocusColor : '#d1d5db', resize: isMultiline ? 'vertical' : undefined };
  const cleanLabel = stripRequiredMark(label);

  const parsedInitial = useMemo(() => parseBilingualValue(value), []);
  const [mr, setMr] = useState(parsedInitial.mr);
  const [en, setEn] = useState(parsedInitial.en);
  const lastEmittedRef = useRef(value);

  useEffect(() => {
    if (value !== lastEmittedRef.current) {
      const nextParsed = parseBilingualValue(value);
      setMr(nextParsed.mr);
      setEn(nextParsed.en);
      lastEmittedRef.current = value;
    }
  }, [value]);

  const updateCombined = (nextMr, nextEn) => {
    setMr(nextMr);
    setEn(nextEn);
    const hasMr = Boolean(nextMr && nextMr.trim());
    const hasEn = Boolean(nextEn && nextEn.trim());
    let combined = '';
    if (hasMr && hasEn) {
      combined = `${nextMr} / ${nextEn}`;
    } else if (hasMr) {
      combined = nextMr;
    } else if (hasEn) {
      combined = nextEn;
    }
    lastEmittedRef.current = combined;
    onChange(combined);
  };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <label style={labelStyle}>
          {cleanLabel}
          {required && <span style={{ color: '#dc2626', marginLeft: 4, fontWeight: 700 }}>*</span>}
        </label>
        <span style={{ fontSize: '0.72rem', background: '#e0f2fe', color: '#0369a1', padding: '2px 8px', borderRadius: 6, fontWeight: 700 }}>
          🌐 दोन्ही भाषेत आवश्यक (Bilingual)
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 12 }}>
        {/* Marathi Input */}
        <div>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#0f766e', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span>🚩 मराठी नाव / माहिती</span>
            {required && <span style={{ color: '#dc2626' }}>*</span>}
          </div>
          {isMultiline ? (
            <textarea
              data-marathi-input="true"
              lang="mr"
              rows={2}
              value={mr}
              onChange={e => updateCombined(e.target.value, en)}
              required={required}
              readOnly={readOnly}
              placeholder={placeholder ? (placeholder + ' (मराठीत)') : 'उदा. नाव (मराठीत)'}
              style={mrStyle}
              onFocus={() => setFocusedMr(true)}
              onBlur={() => setFocusedMr(false)}
            />
          ) : (
            <input
              type="text"
              data-marathi-input="true"
              lang="mr"
              value={mr}
              onChange={e => updateCombined(e.target.value, en)}
              required={required}
              readOnly={readOnly}
              placeholder={placeholder ? (placeholder + ' (मराठीत)') : 'उदा. नाव (मराठीत)'}
              style={mrStyle}
              onFocus={() => setFocusedMr(true)}
              onBlur={() => setFocusedMr(false)}
            />
          )}
          <small style={{ color: '#64748b', fontSize: '0.72rem', display: 'block', marginTop: 2 }}>
            इंग्रजीत टाईप केल्यास आपोआप मराठी होईल
          </small>
        </div>

        {/* English Input */}
        <div>
          <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#1e40af', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
            <span>🔤 इंग्रजी नाव / माहिती (In English)</span>
            {required && <span style={{ color: '#dc2626' }}>*</span>}
          </div>
          {isMultiline ? (
            <textarea
              data-marathi-input="false"
              lang="en"
              rows={2}
              value={en}
              onChange={e => updateCombined(mr, e.target.value)}
              required={required}
              readOnly={readOnly}
              placeholder={placeholder ? (placeholder + ' (in English)') : 'e.g. Name (in English)'}
              style={enStyle}
              onFocus={() => setFocusedEn(true)}
              onBlur={() => setFocusedEn(false)}
            />
          ) : (
            <input
              type="text"
              data-marathi-input="false"
              lang="en"
              value={en}
              onChange={e => updateCombined(mr, e.target.value)}
              required={required}
              readOnly={readOnly}
              placeholder={placeholder ? (placeholder + ' (in English)') : 'e.g. Name (in English)'}
              style={enStyle}
              onFocus={() => setFocusedEn(true)}
              onBlur={() => setFocusedEn(false)}
            />
          )}
          <small style={{ color: '#64748b', fontSize: '0.72rem', display: 'block', marginTop: 2 }}>
            Strictly in English (No transliteration)
          </small>
        </div>
      </div>
      {error && <small style={{ color: '#dc2626', fontWeight: 600, display: 'block', marginTop: 4 }}>{error}</small>}
      {!error && helperText && <small style={{ color: '#64748b', fontWeight: 600, display: 'block', marginTop: 4 }}>{helperText}</small>}
    </div>
  );
}

function Field({ label, required, type = 'text', options, value, onChange, placeholder, error, inputMode, minLength, maxLength, min, max, pattern, errorMessage, readOnly, helperText, isBilingual }) {
  const [focused, setFocused] = useState(false);
  const style = { ...fieldStyle, borderColor: error ? '#dc2626' : focused ? fieldFocusColor : '#d1d5db' };
  const cleanLabel = stripRequiredMark(label);

  const renderLabel = () => (
    <label style={labelStyle}>
      {cleanLabel}
      {required && <span style={{ color: '#dc2626', marginLeft: 4, fontWeight: 700 }}>*</span>}
    </label>
  );

  if (isBilingual && (type === 'text' || type === 'textarea')) {
    return (
      <BilingualField
        label={label}
        required={required}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        error={error}
        readOnly={readOnly}
        helperText={helperText}
      />
    );
  }

  if (type === 'checkbox') {
    return (
      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.92rem', fontWeight: 600, color: '#374151' }}>
          <input type="checkbox" checked={Boolean(value)} onChange={e => onChange(e.target.checked)} style={{ width: 18, height: 18 }} />
          <span>
            {cleanLabel}
            {required && <span style={{ color: '#dc2626', marginLeft: 4, fontWeight: 700 }}>*</span>}
          </span>
        </label>
        {error && <small style={{ color: '#dc2626', fontWeight: 600, display: 'block', marginTop: 4 }}>{error}</small>}
      </div>
    );
  }

  if (type === 'select') {
    return (
      <div style={{ marginBottom: 14 }}>
        {renderLabel()}
        <select value={value || ''} onChange={e => onChange(e.target.value)} required={required}
          style={style} onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}>
          <option value="">-- निवडा --</option>
          {(options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {error && <small style={{ color: '#dc2626', fontWeight: 600, display: 'block', marginTop: 4 }}>{error}</small>}
      </div>
    );
  }
  if (type === 'textarea') {
    return (
      <div style={{ marginBottom: 14 }}>
        {renderLabel()}
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} required={required}
          rows={3} placeholder={placeholder} style={{ ...style, resize: 'vertical' }}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
        {error && <small style={{ color: '#dc2626', fontWeight: 600, display: 'block', marginTop: 4 }}>{error}</small>}
      </div>
    );
  }
  const inputType = certificateInputType(type);
  const nextInputMode = type === 'number' ? 'decimal' : inputMode;
  return (
    <div style={{ marginBottom: 14 }}>
      {renderLabel()}
      <input type={inputType} value={value || ''} onChange={e => {
        const nextValue = type === 'number' ? toEnglishDigits(e.target.value) : e.target.value;
        if (type === 'number' && /[+-]/.test(nextValue)) return;
        onChange(nextValue);
      }} onKeyDown={e => { if (type === 'number' && ['e', 'E', '+', '-'].includes(e.key)) e.preventDefault(); }}
        required={required} readOnly={readOnly || type === 'calculated'}
        placeholder={placeholder} style={style} inputMode={nextInputMode} minLength={minLength} maxLength={maxLength} min={min} max={max}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)} />
      {error && <small style={{ color: '#dc2626', fontWeight: 600, display: 'block', marginTop: 4 }}>{error}</small>}
      {!error && helperText && <small style={{ color: '#64748b', fontWeight: 600, display: 'block', marginTop: 4 }}>{helperText}</small>}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────
const documentUrl = url => {
  if (!url || url === '#') return '#';
  if (/^https?:\/\//i.test(url)) return url;
  return `${API_ORIGIN}${url.startsWith('/') ? '' : '/'}${url}`;
};

export default function CitizenApplyPage() {
  const { tenantSlug } = useParams();
  const [searchParams] = useSearchParams();
  const { user, isAuthenticated, loading } = useAuth();
  const { tenantData, loading: tenantLoading } = useTenant();
  const fileInputRefs = useRef({});
  const initialTypeHandled = useRef(false);
  const previewObjectUrls = useRef(new Set());
  const pendingUploadReceipts = useRef(new Set());

  const initialType = searchParams.get('type') || '';

  const [certType, setCertType] = useState(initialType);
  const [applicantName, setApplicantName] = useState('');
  const [applicantPhone, setApplicantPhone] = useState('');
  const [applicantAddress, setApplicantAddress] = useState('');
  const [showEditApplicant, setShowEditApplicant] = useState(false);
  const [purpose, setPurpose] = useState('');
  const [typeFields, setTypeFields] = useState({});
  const [customFieldsData, setCustomFieldsData] = useState({});
  const [documents, setDocuments] = useState({});
  const [customDocs, setCustomDocs] = useState([]);
  const [newDocName, setNewDocName] = useState('');
  const [success, setSuccess] = useState('');
  const [successUid, setSuccessUid] = useState('');
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);
  const [paymentAvailability, setPaymentAvailability] = useState({ loading: true, available: false, key_id: null });
  const [paymentChoice, setPaymentChoice] = useState('online');
  const [serviceFees, setServiceFees] = useState(null);
  const [feesLoading, setFeesLoading] = useState(true);
  const [taxEligibility, setTaxEligibility] = useState({
    loading: true,
    eligible: true,
    has_pending: false,
    total_pending_amount: 0,
    household_pending_amount: 0,
    business_pending_amount: 0,
    groups: [],
    items: [],
    pay_url: '',
  });

  const checkTaxStatus = useCallback(() => {
    if (!tenantSlug) return;
    setTaxEligibility(prev => ({ ...prev, loading: true }));
    certificateAPI.checkTaxEligibility(tenantSlug)
      .then(res => {
        if (res.data) {
          setTaxEligibility({
            loading: false,
            eligible: res.data.eligible !== false,
            has_pending: Boolean(res.data.has_pending),
            total_pending_amount: Number(res.data.total_pending_amount || 0),
            household_pending_amount: Number(res.data.household_pending_amount || 0),
            business_pending_amount: Number(res.data.business_pending_amount || 0),
            groups: res.data.groups || [],
            items: res.data.items || [],
            pay_url: res.data.pay_url || `/gp/${tenantSlug}/my-taxes`,
          });
        }
      })
      .catch(() => {
        setTaxEligibility(prev => ({ ...prev, loading: false }));
      });
  }, [tenantSlug]);

  useEffect(() => {
    checkTaxStatus();
  }, [checkTaxStatus]);
  useEffect(() => {
    if (!tenantSlug) return;
    certificateAPI.getPaymentAvailability(tenantSlug)
      .then(res => {
        setPaymentAvailability({
          loading: false,
          available: Boolean(res.data?.available),
          key_id: res.data?.key_id || null,
        });
      })
      .catch(() => {
        setPaymentAvailability({ loading: false, available: false, key_id: null });
      });
  }, [tenantSlug]);

  const revokePendingReceipt = useCallback(receipt => {
    if (!receipt) return;
    pendingUploadReceipts.current.delete(receipt);
    certificateAPI.revokePendingUpload(tenantSlug, receipt).catch(() => {});
  }, [tenantSlug]);

  const revokeAllPendingReceipts = useCallback(() => {
    const receipts = [...pendingUploadReceipts.current];
    pendingUploadReceipts.current.clear();
    receipts.forEach(receipt => certificateAPI.revokePendingUpload(tenantSlug, receipt).catch(() => {}));
  }, [tenantSlug]);

  const activeFees = useMemo(
    () => (serviceFees ?? tenantData?.fees ?? []).filter(fee => fee?.is_active !== false && (fee?.service_provider || 'grampanchayat') === 'grampanchayat' && fee?.can_issue_certificate !== false),
    [serviceFees, tenantData?.fees],
  );
  const availableCertTypes = useMemo(() => activeFees.map(fee => ({
    type: fee.service_type,
    label: fee.service_name || fee.service_type?.replaceAll('_', ' '),
    icon: fee.display_icon || '📄',
  })).filter(type => type.type), [activeFees]);
  const isCertAvailable = availableCertTypes.some(t => t.type === certType);
  const currentCert = availableCertTypes.find(t => t.type === certType) || availableCertTypes[0] || { type: '', label: 'प्रमाणपत्र', icon: '📄' };
  const selectedFee = activeFees.find(fee => fee.service_type === currentCert.type);
  const feeDocuments = normalizeDocumentRequirements(selectedFee?.required_documents);
  
  // Section-wise dynamic form fields (configured by admin)
  const dynamicFormSections = useMemo(() => {
    const raw = selectedFee?.custom_form_fields;
    return normalizeCustomFormFields(raw);
  }, [selectedFee]);

  const configuredCustomFields = useMemo(() => {
    return dynamicFormSections.flatMap(sec => sec.fields || []);
  }, [dynamicFormSections]);
  const resolvedTypeFields = useMemo(
    () => resolveCertificateValues(dynamicFormSections, typeFields),
    [dynamicFormSections, typeFields],
  );

  const currentDocuments = feeDocuments;
  const requiredDocuments = currentDocuments.filter(doc => doc.is_required !== false);
  const currentProcessingTime = selectedFee?.processing_time || 'कागदपत्र पडताळणीनंतर मंजुरी';

  // Guidelines calculation: 100% dynamic from admin public_instructions
  const certGuidelines = useMemo(() => {
    const rawInstr = selectedFee?.public_instructions;
    if (rawInstr && rawInstr.trim()) {
      try {
        const parsed = JSON.parse(rawInstr);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      } catch {
        // Raw text line format
      }
      const lines = rawInstr.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
      if (lines.length > 0) {
        return lines.map((line, idx) => {
          const parts = line.split(/:(.+)/);
          if (parts.length >= 2 && parts[0].trim()) {
            return { title: parts[0].trim(), text: parts[1].trim() };
          }
          return { title: `📌 सुचना ${idx + 1}`, text: line };
        });
      }
    }
    return [];
  }, [selectedFee]);

  // Auto-fill from logged-in profile
  useEffect(() => {
    if (user) {
      setApplicantName(prev => prev || user.full_name || '');
      setApplicantPhone(prev => prev || normalizePhoneInput(user.phone || ''));
      setApplicantAddress(prev => prev || user.address || 'देऊळगाव गाडा');
    }
  }, [user]);

  // Load the canonical active service list for this form. The larger public
  // portal payload can be stale during a route transition, which previously
  // left step 1 with an empty certificate selector.
  useEffect(() => {
    let cancelled = false;
    setFeesLoading(true);
    contentAPI.getFees(tenantSlug)
      .then(response => {
        if (!cancelled) setServiceFees(response.data?.fees || []);
      })
      .catch(() => {
        if (!cancelled) setServiceFees(tenantData?.fees || []);
      })
      .finally(() => {
        if (!cancelled) setFeesLoading(false);
      });

    return () => { cancelled = true; };
  }, [tenantSlug, tenantData?.fees]);

  useEffect(() => {
    if (tenantLoading || feesLoading || !tenantData || initialTypeHandled.current || !availableCertTypes.length) return;
    initialTypeHandled.current = true;

    const requested = searchParams.get('type');
    const requestedAvailable = requested && availableCertTypes.some(type => type.type === requested);
    const nextType = requestedAvailable ? requested : availableCertTypes[0].type;
    setCertType(nextType);
    setStep(requestedAvailable ? 2 : 1);
  }, [tenantLoading, feesLoading, tenantData, availableCertTypes, searchParams]);

  // Reset fields when cert type changes
  useEffect(() => {
    revokeAllPendingReceipts();
    previewObjectUrls.current.forEach(url => URL.revokeObjectURL(url));
    previewObjectUrls.current.clear();
    setTypeFields({});
    setCustomFieldsData({});
    setDocuments({});
    setCustomDocs([]);
    setNewDocName('');
    setFieldErrors({});
  }, [certType, revokeAllPendingReceipts]);

  useEffect(() => () => {
    revokeAllPendingReceipts();
    previewObjectUrls.current.forEach(url => URL.revokeObjectURL(url));
    previewObjectUrls.current.clear();
  }, [revokeAllPendingReceipts]);

  if (loading || tenantLoading || feesLoading) return <div style={{ padding: '60px 20px', textAlign: 'center', color: '#888' }}>लोड होत आहे...</div>;
  if (!isAuthenticated) return <Navigate to={`/login`} replace />;

  const handleDocUpload = async (docName, file) => {
    if (!file) return;
    if (!isAllowedDocumentFile(file)) {
      const uploadError = documentFileValidationError(file) === 'size'
        ? `फाईल आकार ${MAX_DOCUMENT_UPLOAD_BYTES / (1024 * 1024)}MB किंवा कमी असावा`
        : 'केवळ JPG, PNG, WebP किंवा PDF फाईल अपलोड करा.';
      setDocuments(prev => ({
        ...prev,
        [docName]: {
          ...prev[docName],
          uploading: false,
          error: true,
          errorMsg: uploadError,
        },
      }));
      return;
    }
    const previousDocument = documents[docName];
    setDocuments(prev => ({ ...prev, [docName]: { ...prev[docName], uploading: true, error: false } }));
    try {
      const fd = new FormData();
      fd.append('document', file);
      const res = await certificateAPI.uploadDoc(tenantSlug, fd);
      const previewUrl = URL.createObjectURL(file);
      previewObjectUrls.current.add(previewUrl);
      pendingUploadReceipts.current.add(res.data.uploadReceipt);
      if (previousDocument?.previewUrl) {
        URL.revokeObjectURL(previousDocument.previewUrl);
        previewObjectUrls.current.delete(previousDocument.previewUrl);
      }
      revokePendingReceipt(previousDocument?.uploadReceipt);
      setDocuments(prev => ({
        ...prev,
        [docName]: {
          url: res.data.url,
          uploadReceipt: res.data.uploadReceipt,
          mimeType: res.data.mimeType,
          previewUrl,
          originalName: res.data.originalName || file.name,
          label: docName,
          uploading: false,
          error: false,
        },
      }));
    } catch (err) {
      const msg = err.response?.data?.error || 'अपलोड अयशस्वी';
      setDocuments(prev => ({ ...prev, [docName]: { ...prev[docName], uploading: false, error: true, errorMsg: msg } }));
    }
  };

  const handleCustomDocUpload = async (docId, file) => {
    if (!file) return;
    if (!isAllowedDocumentFile(file)) {
      const uploadError = documentFileValidationError(file) === 'size'
        ? `Max ${MAX_DOCUMENT_UPLOAD_BYTES / (1024 * 1024)}MB`
        : 'Only JPG, PNG, WebP, or PDF files are accepted.';
      setCustomDocs(prev => prev.map(d => d.id === docId ? { ...d, uploading: false, error: true, errorMsg: uploadError } : d));
      return;
    }
    const previousDocument = customDocs.find(document => document.id === docId);
    setCustomDocs(prev => prev.map(d => d.id === docId ? { ...d, uploading: true, error: false } : d));
    try {
      const fd = new FormData();
      fd.append('document', file);
      const res = await certificateAPI.uploadDoc(tenantSlug, fd);
      const previewUrl = URL.createObjectURL(file);
      previewObjectUrls.current.add(previewUrl);
      pendingUploadReceipts.current.add(res.data.uploadReceipt);
      revokePendingReceipt(previousDocument?.uploadReceipt);
      setCustomDocs(prev => prev.map(d => {
        if (d.id !== docId) return d;
        if (d.previewUrl) {
          URL.revokeObjectURL(d.previewUrl);
          previewObjectUrls.current.delete(d.previewUrl);
        }
        return { ...d, url: res.data.url, previewUrl, uploadReceipt: res.data.uploadReceipt, mimeType: res.data.mimeType, originalName: res.data.originalName || file.name, uploading: false };
      }));
    } catch (err) {
      const msg = err.response?.data?.error || 'अपलोड अयशस्वी';
      setCustomDocs(prev => prev.map(d => d.id === docId ? { ...d, uploading: false, error: true, errorMsg: msg } : d));
    }
  };

  const addCustomDoc = () => {
    const name = newDocName.trim();
    if (!name || currentDocuments.length + customDocs.length >= MAX_CERTIFICATE_DOCUMENTS) return;
    setCustomDocs(prev => [...prev, { id: Date.now(), name, url: null, originalName: null, uploading: false }]);
    setNewDocName('');
  };

  const removeCustomDoc = (docId) => {
    revokePendingReceipt(customDocs.find(document => document.id === docId)?.uploadReceipt);
    setCustomDocs(prev => {
      const removed = prev.find(d => d.id === docId);
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
        previewObjectUrls.current.delete(removed.previewUrl);
      }
      return prev.filter(d => d.id !== docId);
    });
  };

  const removeDoc = (docName) => {
    revokePendingReceipt(documents[docName]?.uploadReceipt);
    setDocuments(prev => {
      const n = { ...prev };
      if (n[docName]?.previewUrl) {
        URL.revokeObjectURL(n[docName].previewUrl);
        previewObjectUrls.current.delete(n[docName].previewUrl);
      }
      delete n[docName];
      return n;
    });
  };

  const validateStep = (targetStep = step) => {
    const nextErrors = {};

    if (targetStep === 1 && !isCertAvailable) {
      nextErrors.certificate_type = 'कृपया ग्रामपंचायतीने ऑनलाइन उपलब्ध केलेला प्रमाणपत्र प्रकार निवडा.';
    }

    if (targetStep === 2) {
      if (!applicantName.trim()) nextErrors.applicantName = requiredMessage('अर्जदाराचे नाव');
      if (!isValidPhone(applicantPhone)) nextErrors.applicantPhone = phoneMessage;

      dynamicFormSections.forEach(sec => {
        (sec.fields || []).forEach(field => {
          if (!isCertificateFieldVisible(field, resolvedTypeFields)) return;
          const value = resolvedTypeFields[field.key];
          const valStr = String(value || '').trim();
          const cleanLabel = stripRequiredMark(field.label);

          const customErr = field.errorMessage || field.error_message;

          if (isCertificateFieldRequired(field, resolvedTypeFields) && !valStr) {
            nextErrors[field.key] = customErr || requiredMessage(cleanLabel);
            return;
          }

          if (field.is_bilingual && isCertificateFieldRequired(field, resolvedTypeFields)) {
            const parts = valStr.split('/').map(s => s.trim()).filter(Boolean);
            if (parts.length < 2) {
              nextErrors[field.key] = customErr || `${cleanLabel} मराठी व इंग्रजी दोन्ही भाषेत भरणे आवश्यक आहे.`;
              return;
            }
          }

          if (valStr && field.minLength != null && valStr.length < Number(field.minLength)) {
            nextErrors[field.key] = customErr || `${cleanLabel} किमान ${field.minLength} अक्षरे / अंकी असावे.`;
            return;
          }

          if (valStr && field.maxLength != null && valStr.length > Number(field.maxLength)) {
            nextErrors[field.key] = customErr || `${cleanLabel} कमाल ${field.maxLength} अक्षरांपेक्षा जास्त नसावे.`;
            return;
          }

          if (valStr && field.min != null && Number(valStr) < Number(field.min)) {
            nextErrors[field.key] = customErr || `${cleanLabel} किमान मूल्य ${field.min} असावे.`;
            return;
          }

          if (valStr && field.max != null && Number(valStr) > Number(field.max)) {
            nextErrors[field.key] = customErr || `${cleanLabel} कमाल मूल्य ${field.max} असावे.`;
            return;
          }

          if (field.key === 'groom_aadhaar' && valStr && valStr.length !== 12) {
            nextErrors.groom_aadhaar = 'वराचा आधार क्रमांक बरोबर १२ अंकी असावा.';
          }
          if (field.key === 'bride_aadhaar' && valStr && valStr.length !== 12) {
            nextErrors.bride_aadhaar = 'वधूचा आधार क्रमांक बरोबर १२ अंकी असावा.';
          }
        });
      });
    }

    if (targetStep === 3) {
      const missingDocs = requiredDocuments.filter(doc => !documents[doc.name]?.url);
      if (missingDocs.length) {
        nextErrors.documents = `कृपया आवश्यक कागदपत्रे अपलोड करा: ${missingDocs.map(doc => doc.name).join(', ')}`;
      }
    }

    return nextErrors;
  };

  const goNext = () => {
    if (!taxEligibility.eligible) {
      setError('प्रमाणपत्र अर्जासाठी आधी थकीत कर भरणे आवश्यक आहे. कृपया आपल्या घर किंवा व्यवसायाचा कर भरा.');
      return;
    }
    const validationErrors = validateStep(step);
    setFieldErrors(validationErrors);
    if (Object.keys(validationErrors).length) return;
    setError('');
    setStep(s => s + 1);
  };

  const handleSubmit = async () => {
    setError(''); setSuccess(''); setSuccessUid('');
    if (!taxEligibility.eligible) {
      setError('प्रमाणपत्र अर्जासाठी आधी थकीत कर भरणे आवश्यक आहे. कृपया आपल्या घर किंवा व्यवसायाचा कर भरा.');
      return;
    }
    const validationErrors = { ...validateStep(2), ...validateStep(3) };
    if (Object.keys(validationErrors).length) {
      setFieldErrors(validationErrors);
      setStep(validationErrors.documents ? 3 : 2);
      return;
    }
    if (!isCertAvailable) {
      setError('हा प्रमाणपत्र प्रकार सध्या ऑनलाइन उपलब्ध नाही. कृपया ग्रामपंचायत कार्यालयाशी संपर्क साधा.');
      setStep(1);
      return;
    }
    setSubmitting(true);
    try {
      const feeAmount = Number(selectedFee?.fee_amount || 0);
      let paymentData = null;

      if (feeAmount > 0 && paymentAvailability.available && paymentChoice === 'online') {
        const orderRes = await certificateAPI.createPaymentOrder(tenantSlug, {
          certificate_type: certType,
        });
        const orderData = orderRes.data;
        const razorpayLoaded = await loadRazorpay();
        if (!razorpayLoaded) {
          throw new Error('सुरक्षित भरणा खिडकी उघडता आली नाही. कृपया पुन्हा प्रयत्न करा किंवा कार्यालयात रोख भरणा पर्याय निवडा.');
        }

        paymentData = await new Promise((resolve, reject) => {
          const rzp = new window.Razorpay({
            key: orderData.key_id,
            amount: orderData.amount,
            currency: orderData.currency || 'INR',
            name: tenantData?.name || 'ग्रामपंचायत',
            description: `${currentCert.label} अर्ज शुल्क`,
            order_id: orderData.order_id,
            prefill: {
              name: applicantName || user?.full_name,
              contact: applicantPhone || user?.phone,
              email: user?.email || '',
            },
            notes: {
              tenant_slug: tenantSlug,
              certificate_type: certType,
            },
            handler: (response) => {
              resolve({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              });
            },
            modal: {
              ondismiss: () => {
                reject(new Error('भरणा प्रक्रिया रद्द केली. अर्ज सादर झालेला नाही.'));
              },
            },
            theme: { color: '#0284c7' },
          });
          rzp.open();
        });
      }

      const docList = [
        ...Object.entries(documents)
          .filter(([, v]) => v.url)
          .map(([name, v]) => {
            const requirement = currentDocuments.find(doc => doc.name === name);
            return { name, code: requirement?.code, label: name, uploadReceipt: v.uploadReceipt, originalName: v.originalName };
          }),
        ...customDocs
          .filter(d => d.url)
          .map(d => ({ name: d.name, label: d.name, uploadReceipt: d.uploadReceipt, originalName: d.originalName })),
      ];

      const enrichedFields = { ...resolvedTypeFields };
      configuredCustomFields.forEach(field => {
        if (field.is_bilingual && enrichedFields[field.key]) {
          const val = String(enrichedFields[field.key]);
          if (val.includes('/')) {
            const parts = val.split('/').map(s => s.trim());
            enrichedFields[`${field.key}_mr`] = parts[0] || '';
            enrichedFields[`${field.key}_en`] = parts.slice(1).join('/') || '';
          }
        }
      });

      const payload = {
        certificate_type: certType,
        subject_name: certType === 'marriage' ? `${typeFields.groom_name} व ${typeFields.bride_name}` : applicantName,
        application_data: {
          applicant_name: applicantName,
          applicant_phone: applicantPhone,
          applicant_address: applicantAddress,
          purpose,
          ...enrichedFields,
          custom_fields: customFieldsData,
        },
        documents: docList,
        ...(paymentData ? { payment: paymentData } : {}),
      };
      const res = await certificateAPI.create(tenantSlug, payload);
      pendingUploadReceipts.current.clear();
      const uid = res.data?.application?.application_uid || res.data?.application_uid || '';
      setSuccessUid(uid);
      setSuccess('अर्ज यशस्वीरीत्या सादर झाला!');
    } catch (err) {
      if (err.response?.data?.code === 'TAX_PAYMENT_REQUIRED') {
        const summary = err.response.data.tax_summary || {};
        setTaxEligibility({
          loading: false,
          eligible: false,
          has_pending: true,
          total_pending_amount: Number(summary.total_pending_amount || 0),
          household_pending_amount: Number(summary.household_pending_amount || 0),
          business_pending_amount: Number(summary.business_pending_amount || 0),
          items: summary.items || [],
          pay_url: summary.pay_url || `/gp/${tenantSlug}/my-taxes`,
        });
        setError(err.response.data.error || 'प्रमाणपत्र अर्जासाठी आधी थकीत कर भरणे आवश्यक आहे.');
        return;
      }
      setError(err.response?.data?.error || 'सादर करता आली नाही. कृपया पुन्हा प्रयत्न करा.');
    } finally {
      setSubmitting(false);
    }
  };

  const steps = [
    { n: 1, label: 'प्रमाणपत्र प्रकार' },
    { n: 2, label: 'अर्जदार माहिती' },
    { n: 3, label: 'कागदपत्रे' },
    { n: 4, label: 'पुनरावलोकन' },
  ];

  if (success) {
    return (
      <div style={{ maxWidth: 600, margin: '60px auto', padding: '0 16px', textAlign: 'center' }}>
        <div style={{ background: '#fff', borderRadius: 16, padding: '48px 32px', boxShadow: '0 4px 24px rgba(0,0,0,0.08)' }}>
          <div style={{ fontSize: '3.5rem', marginBottom: 16 }}>✅</div>
          <h2 style={{ color: '#1a5276', margin: '0 0 8px', fontSize: '1.5rem' }}>अर्ज यशस्वीरीत्या सादर झाला!</h2>
          {successUid && (
            <p style={{ background: '#fdf6ee', border: '1px solid #f5cba7', borderRadius: 10, padding: '12px 18px', margin: '16px 0', fontSize: '1rem' }}>
              अर्ज आयडी: <strong style={{ fontFamily: 'monospace', color: '#e67e22', fontSize: '1.15rem' }}>{successUid}</strong>
            </p>
          )}
          <p style={{ color: '#666', lineHeight: 1.7, marginBottom: 24 }}>
            तुमचा अर्ज ग्रामपंचायत कार्यालयाला पाठवला गेला आहे.<br />तुम्ही तुमच्या खात्यातून अर्जाची स्थिती तपासू शकता.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to={`/gp/${tenantSlug}/dashboard`}
              style={{ padding: '11px 28px', background: '#1a5276', color: '#fff', borderRadius: 8, textDecoration: 'none', fontWeight: 700, fontSize: '0.95rem' }}>
              माझे अर्ज पहा
            </Link>
            <button onClick={() => { setSuccess(''); setSuccessUid(''); setStep(1); setTypeFields({}); setCustomFieldsData({}); setDocuments({}); setPurpose(''); }}
              style={{ padding: '11px 28px', background: '#fff', color: '#1a5276', border: '2px solid #1a5276', borderRadius: 8, fontWeight: 700, fontSize: '0.95rem', cursor: 'pointer' }}>
              नवीन अर्ज करा
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="citizen-apply-page" style={{ maxWidth: 1240, margin: '30px auto', padding: '0 16px' }}>
      <style>{`
        .apply-page-grid {
          display: grid;
          grid-template-columns: minmax(0, 1fr) 340px;
          gap: 24px;
          align-items: start;
        }
        .form-two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 0 16px;
        }
        .citizen-certificate-section {
          --section-accent: #0369a1;
          --section-accent-dark: #075985;
          --section-header-bg: #dff2fc;
          --section-body-bg: #f5fbff;
          --section-border: #a9d9f2;
          margin-bottom: 18px;
          overflow: hidden;
          border: 1.5px solid var(--section-border);
          border-radius: 12px;
          background: var(--section-body-bg);
          box-shadow: 0 4px 14px rgba(30, 90, 125, 0.06);
        }
        .citizen-certificate-section--alternate {
          --section-accent: #86198f;
          --section-accent-dark: #701a75;
          --section-header-bg: #f7ddfb;
          --section-body-bg: #fff8ff;
          --section-border: #eab8f1;
        }
        .citizen-certificate-section__header {
          display: flex;
          align-items: center;
          gap: 10px;
          min-height: 52px;
          padding: 10px 18px;
          border-bottom: 1px solid var(--section-border);
          border-left: 5px solid var(--section-accent);
          background: linear-gradient(110deg, var(--section-header-bg), rgba(255, 255, 255, 0.72));
          color: var(--section-accent-dark);
          font-size: 1rem;
          font-weight: 850;
          letter-spacing: 0.01em;
        }
        .citizen-certificate-section__icon {
          display: inline-grid;
          flex: 0 0 34px;
          width: 34px;
          height: 34px;
          place-items: center;
          border: 1px solid color-mix(in srgb, var(--section-accent) 28%, white);
          border-radius: 9px;
          background: rgba(255, 255, 255, 0.82);
          box-shadow: 0 2px 7px rgba(38, 70, 92, 0.08);
          font-size: 1.05rem;
        }
        .citizen-certificate-section__body {
          padding: 18px 20px 4px;
        }
        @media (max-width: 900px) {
          .apply-page-grid {
            grid-template-columns: 1fr !important;
          }
          .form-two-col {
            grid-template-columns: 1fr !important;
          }
          .apply-page-header {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 12px !important;
          }
          .apply-page-card-padding {
            padding: 18px 16px !important;
          }
          .citizen-certificate-section__header {
            padding: 9px 13px;
          }
          .citizen-certificate-section__body {
            padding: 16px 14px 2px;
          }
        }
        @media (max-width: 600px) {
          .citizen-apply-page {
            margin: 12px auto !important;
            padding: 0 4px !important;
          }
          .apply-stepper {
            padding: 12px 8px !important;
            overflow-x: auto;
          }
          .apply-document-row,
          .apply-custom-document-row {
            align-items: stretch !important;
            flex-direction: column;
          }
          .apply-document-row > div,
          .apply-custom-document-row > div {
            min-width: 0 !important;
            overflow-wrap: anywhere;
          }
          .apply-document-actions,
          .apply-custom-document-input,
          .apply-page-footer {
            align-items: stretch !important;
            flex-direction: column;
          }
          .apply-document-actions > button,
          .apply-custom-document-input > button,
          .apply-page-footer > button {
            width: 100%;
          }
          .apply-page-footer {
            gap: 10px;
            padding: 16px !important;
          }
        }
      `}</style>

      {/* Page Header */}
      <div className="apply-page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, paddingBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
        <div>
          <Link to={'/gp/' + tenantSlug + '/dashboard'} style={{ color: '#1a5276', textDecoration: 'none', fontSize: '0.88rem', fontWeight: 600 }}>
            ← डॅशबोर्डवर परत जा
          </Link>
          <h1 style={{ margin: '6px 0 0', color: '#1a5276', fontSize: '1.75rem', fontWeight: 800 }}>प्रमाणपत्रासाठी ऑनलाइन अर्ज</h1>
        </div>
        <div style={{ textAlign: 'right' }}>
          <span style={{ background: '#fef3c7', color: '#92400e', padding: '6px 14px', borderRadius: 20, fontSize: '0.82rem', fontWeight: 800 }}>
            🏛️ {tenantData?.name || 'ग्रामपंचायत'}
          </span>
          <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: '0.86rem' }}>अर्जदार खाते: <strong style={{ color: '#0f172a' }}>{user?.full_name}</strong></p>
        </div>
      </div>

      {/* 2-Column Responsive Layout Grid */}
      <div className="apply-page-grid">

        {/* Left Column: Main Application Form or Pending Tax Block */}
        <div>
          {error && (
            <div style={{ background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 10, padding: '12px 18px', marginBottom: 18, color: '#991b1b', fontWeight: 600, fontSize: '0.9rem' }}>
              {error}
            </div>
          )}

          {!taxEligibility.eligible && taxEligibility.has_pending ? (
            <div className="tax-pending-portal-card" style={{
              background: '#fff',
              borderRadius: 16,
              border: '2px solid #f59e0b',
              boxShadow: '0 6px 24px rgba(245, 158, 11, 0.12)',
              overflow: 'hidden',
              marginBottom: 24,
            }}>
              {/* Header Banner */}
              <div style={{
                background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
                padding: '24px 28px',
                borderBottom: '1.5px solid #fde68a',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <div style={{
                    width: 50,
                    height: 50,
                    borderRadius: 12,
                    background: '#fde68a',
                    border: '1.5px solid #f59e0b',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '1.8rem',
                    flexShrink: 0,
                  }}>
                    ⚠️
                  </div>
                  <div>
                    <h2 style={{ margin: 0, color: '#92400e', fontSize: '1.3rem', fontWeight: 850 }}>
                      थकीत कर प्रलंबित आहे (Pending Tax Payment Required)
                    </h2>
                    <p style={{ margin: '4px 0 0', color: '#78350f', fontSize: '0.9rem', fontWeight: 600 }}>
                      प्रमाणपत्र अर्ज करण्यासाठी थकीत कर भरणा करणे अनिवार्य आहे.
                    </p>
                  </div>
                </div>
                <div style={{
                  background: '#b45309',
                  color: '#fff',
                  padding: '7px 18px',
                  borderRadius: 24,
                  fontSize: '1rem',
                  fontWeight: 850,
                  boxShadow: '0 2px 8px rgba(180, 83, 9, 0.25)',
                }}>
                  एकूण थकीत: ₹{Number(taxEligibility.total_pending_amount || 0).toLocaleString('en-IN')}
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: '24px 28px' }}>
                <div style={{
                  background: '#fffbeb',
                  border: '1px solid #fcd34d',
                  borderRadius: 10,
                  padding: '14px 18px',
                  marginBottom: 22,
                  color: '#78350f',
                  fontSize: '0.94rem',
                  lineHeight: 1.65,
                }}>
                  तुमच्या खात्याशी जोडलेल्या <strong>घराचा / मिळकतीचा किंवा व्यवसायाचा कर प्रलंबित</strong> असल्यामुळे सध्या कोणताही प्रमाणपत्र अर्ज सादर करता येणार नाही. शासकीय नियमांनुसार प्रमाणपत्र मिळवण्यासाठी आधी थकीत कर भरणा करणे अनिवार्य आहे. कर भरल्यानंतर आपण लगेच ऑनलाइन अर्ज करू शकता.
                </div>

                {/* Grouped Property / Business Breakdown */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
                    <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.05rem', fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
                      <span>📋</span> प्रलंबित कर तपशील (Pending Tax Breakdown)
                    </h3>
                    <span style={{ fontSize: '0.84rem', color: '#64748b', fontWeight: 700 }}>
                      {taxEligibility.groups && taxEligibility.groups.length > 0
                        ? (taxEligibility.groups.length + ' खाते / मिळकती')
                        : (((taxEligibility.items && taxEligibility.items.length) || 0) + ' नोंदी')}
                    </span>
                  </div>

                  {taxEligibility.groups && taxEligibility.groups.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                      {taxEligibility.groups.map((grp, gIdx) => (
                        <div key={grp.group_key || gIdx} style={{
                          border: '1.5px solid #e2e8f0',
                          borderRadius: 12,
                          overflow: 'hidden',
                          background: '#fff',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
                        }}>
                          {/* Group Header Card */}
                          <div style={{
                            background: grp.subject_type === 'business' ? '#f0f9ff' : '#f8fafc',
                            borderBottom: '1px solid #e2e8f0',
                            padding: '12px 18px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 10,
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                              <span style={{ fontSize: '1.5rem' }}>{grp.subject_type === 'business' ? '🏪' : '🏠'}</span>
                              <div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                  <strong style={{ color: '#0f172a', fontSize: '1rem', fontWeight: 800 }}>
                                    {grp.subject_name || (grp.subject_type === 'business' ? 'व्यवसाय' : 'मिळकत')}
                                  </strong>
                                  <span style={{
                                    background: grp.subject_type === 'business' ? '#dbeafe' : '#f1f5f9',
                                    color: grp.subject_type === 'business' ? '#1e40af' : '#475569',
                                    fontSize: '0.76rem',
                                    fontWeight: 700,
                                    padding: '2px 8px',
                                    borderRadius: 6,
                                  }}>
                                    {grp.subject_type === 'business' ? 'व्यवसाय कर (Business Tax)' : 'घरपट्टी / मिळकत कर (Property Tax)'}
                                  </span>
                                </div>
                                <div style={{ fontSize: '0.82rem', color: '#64748b', marginTop: 3 }}>
                                  {grp.subject_reference && <span>{grp.subject_reference}</span>}
                                  {grp.subject_address && <span> • 📍 {grp.subject_address}</span>}
                                </div>
                              </div>
                            </div>
                            <div style={{ textAlign: 'right' }}>
                              <div style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>एकूण थकीत</div>
                              <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#dc2626' }}>
                                ₹{Number(grp.total_pending_amount || 0).toLocaleString('en-IN')}
                              </div>
                            </div>
                          </div>

                          {/* Year groups breakdown */}
                          <div style={{ padding: '14px 18px' }}>
                            {grp.year_groups && grp.year_groups.map((yg, yIdx) => (
                              <div key={yIdx} style={{
                                marginBottom: yIdx < grp.year_groups.length - 1 ? 12 : 0,
                                paddingBottom: yIdx < grp.year_groups.length - 1 ? 12 : 0,
                                borderBottom: yIdx < grp.year_groups.length - 1 ? '1px dashed #e2e8f0' : 'none',
                              }}>
                                <div style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  marginBottom: 6,
                                  fontSize: '0.85rem',
                                  fontWeight: 700,
                                }}>
                                  <span style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 6,
                                    background: '#f8fafc',
                                    border: '1px solid #e2e8f0',
                                    padding: '3px 10px',
                                    borderRadius: 6,
                                    color: '#334155',
                                  }}>
                                    📅 आर्थिक वर्ष: {yg.financial_year}
                                  </span>
                                  <span style={{ color: '#b91c1c', fontWeight: 800 }}>
                                    वर्ष एकूण: ₹{Number(yg.total_amount || 0).toLocaleString('en-IN')}
                                  </span>
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, paddingLeft: 8 }}>
                                  {yg.items.map((it, itIdx) => (
                                    <div key={itIdx} style={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      fontSize: '0.86rem',
                                      color: '#334155',
                                      padding: '3px 0',
                                    }}>
                                      <span>• {it.title}</span>
                                      <span style={{ fontWeight: 700, color: '#dc2626' }}>
                                        ₹{Number(it.pending_amount !== undefined ? it.pending_amount : (it.amount || 0)).toLocaleString('en-IN')}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* Fallback to flat items if groups not structured */
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {taxEligibility.items && taxEligibility.items.map((item, idx) => (
                        <div key={idx} style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '10px 14px',
                          background: '#f8fafc',
                          borderRadius: 8,
                          border: '1px solid #e2e8f0',
                        }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <span>{item.category === 'business' ? '🏪' : '🏠'}</span>
                            <div>
                              <strong>{item.title}</strong>
                              {item.financial_year && (
                                <span style={{ marginLeft: 8, fontSize: '0.8rem', color: '#64748b' }}>
                                  (आ.व. {item.financial_year})
                                </span>
                              )}
                            </div>
                          </div>
                          <strong style={{ color: '#dc2626' }}>₹{Number(item.pending_amount || 0).toLocaleString('en-IN')}</strong>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Actions */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 12,
                  paddingTop: 16,
                  borderTop: '1px solid #e2e8f0',
                }}>
                  <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <Link
                      to={taxEligibility.pay_url || ('/gp/' + tenantSlug + '/my-taxes')}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 8,
                        background: '#047857',
                        color: '#fff',
                        padding: '11px 24px',
                        borderRadius: 8,
                        textDecoration: 'none',
                        fontWeight: 800,
                        fontSize: '0.95rem',
                        boxShadow: '0 4px 12px rgba(4, 120, 87, 0.28)',
                      }}
                    >
                      💳 कर आता भरा (Pay Tax Now) →
                    </Link>
                    <button
                      type="button"
                      onClick={checkTaxStatus}
                      disabled={taxEligibility.loading}
                      style={{
                        padding: '10px 18px',
                        background: '#fff',
                        color: '#92400e',
                        border: '1.5px solid #d97706',
                        borderRadius: 8,
                        cursor: taxEligibility.loading ? 'wait' : 'pointer',
                        fontWeight: 700,
                        fontSize: '0.9rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                      }}
                    >
                      🔄 {taxEligibility.loading ? 'तपासत आहे...' : 'कर भरला आहे? स्थिती तपासा (Recheck)'}
                    </button>
                  </div>
                  <Link
                    to={'/gp/' + tenantSlug + '/dashboard'}
                    style={{
                      color: '#64748b',
                      textDecoration: 'none',
                      fontSize: '0.88rem',
                      fontWeight: 600,
                    }}
                  >
                    ← डॅशबोर्डवर परत जा
                  </Link>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* Step indicator */}
              <div className="apply-stepper" style={{ display: 'flex', alignItems: 'center', marginBottom: 24, gap: 0, background: '#fff', padding: '16px 20px', borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.04)', border: '1px solid #e2e8f0' }}>
                {steps.map((s, i) => (
                  <React.Fragment key={s.n}>
                    {i > 0 && <div style={{ flex: 1, height: 2, background: step >= s.n ? '#e67e22' : '#e5e7eb' }} />}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 60 }}>
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: '0.82rem', fontWeight: 700, transition: 'all 0.3s',
                        background: step >= s.n ? '#e67e22' : '#f3f4f6', color: step >= s.n ? '#fff' : '#9ca3af',
                        border: step === s.n ? '2.5px solid #d35400' : '2px solid transparent',
                      }}>{step > s.n ? '✓' : s.n}</div>
                      <span style={{ fontSize: '0.7rem', color: step >= s.n ? '#e67e22' : '#9ca3af', fontWeight: 600, marginTop: 4, textAlign: 'center', lineHeight: 1.2 }}>{s.label}</span>
                    </div>
                  </React.Fragment>
                ))}
              </div>

              <div style={{ background: '#fff', borderRadius: 14, boxShadow: '0 2px 20px rgba(0,0,0,0.07)', overflow: 'hidden', border: '1px solid #e2e8f0' }}>

            {/* ── STEP 1: Certificate Type ──────────────────────────────── */}
            {step === 1 && (
              <div className="apply-page-card-padding" style={{ padding: '28px 28px 20px' }}>
                <h3 style={{ margin: '0 0 4px', color: '#1a5276', fontSize: '1.1rem' }}>प्रमाणपत्र प्रकार निवडा</h3>
                <p style={{ margin: '0 0 18px', color: '#6b7280', fontSize: '0.86rem' }}>
                  फक्त ग्रामपंचायतीने ऑनलाइन उपलब्ध केलेले प्रमाणपत्र प्रकार येथे दिसतील.
                </p>

                <label style={labelStyle}>प्रमाणपत्र प्रकार *</label>
                {availableCertTypes.length > 0 && (
                  <select
                    value={isCertAvailable ? certType : ''}
                    onChange={e => setCertType(e.target.value)}
                    style={{
                      ...fieldStyle, marginBottom: fieldErrors.certificate_type ? 4 : 18,
                      borderColor: fieldErrors.certificate_type ? '#dc2626' : '#d1d5db',
                    }}
                  >
                    {availableCertTypes.map(ct => (
                      <option key={ct.type} value={ct.type}>{ct.icon} {ct.label}</option>
                    ))}
                  </select>
                )}
                {fieldErrors.certificate_type && (
                  <small style={{ color: '#dc2626', fontWeight: 600, display: 'block', margin: '0 0 14px' }}>{fieldErrors.certificate_type}</small>
                )}

                {/* Show required docs info for selected type */}
                {isCertAvailable && currentDocuments.length > 0 && (
                  <div style={{ marginTop: 18, padding: '14px 16px', background: '#eff6ff', borderRadius: 10, border: '1px solid #bfdbfe' }}>
                    <p style={{ margin: '0 0 6px', fontWeight: 700, color: '#1e40af', fontSize: '0.85rem' }}>📎 आवश्यक कागदपत्रे:</p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {currentDocuments.map((d, i) => (
                        <span key={i} style={{ padding: '4px 10px', background: '#dbeafe', borderRadius: 6, fontSize: '0.78rem', color: '#1e40af', fontWeight: 500 }}>
                          {d.name}{d.is_required === false ? ' (Optional)' : ''}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {isCertAvailable && (
                  <div style={{ marginTop: 12, padding: '12px 16px', background: '#f9fafb', borderRadius: 10, border: '1px solid #e5e7eb', color: '#4b5563', fontSize: '0.86rem', lineHeight: 1.6 }}>
                    <strong style={{ color: '#1a5276' }}>शुल्क:</strong> {selectedFee ? (selectedFee.contact_gp_for_fees ? 'शुल्क माहितीसाठी ग्रामपंचायतीशी संपर्क साधा' : (Number(selectedFee.fee_amount || 0) === 0 ? 'मोफत' : ('₹ ' + formatMarathiNumber(selectedFee.fee_amount || 0)))) : 'ग्रामपंचायत नियमानुसार'} ·
                    <strong style={{ color: '#1a5276', marginLeft: 6 }}>प्रक्रिया:</strong> {toMarathiDigits(currentProcessingTime)}.
                  </div>
                )}
              </div>
            )}

            {/* ── STEP 2: Applicant Details + Form Sections ────────────── */}
            {step === 2 && (
              <div className="apply-page-card-padding" style={{ padding: '28px 28px 20px' }}>
                <h3 style={{ margin: '0 0 4px', color: '#1a5276', fontSize: '1.2rem', fontWeight: 800 }}>
                  {currentCert.icon} {currentCert.label} — अर्जाची माहिती
                </h3>
                <p style={{ margin: '0 0 18px', color: '#64748b', fontSize: '0.84rem' }}>* चिन्हांकित सर्व माहिती आवश्यक आहे</p>

                {/* Profile Auto-filled Applicant Banner */}
                <div style={{
                  background: '#f0f9ff', borderRadius: 12, padding: '16px 20px', marginBottom: 20,
                  border: '1.5px solid #7dd3fc', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12
                }}>
                  <div>
                    <div style={{ fontSize: '0.76rem', fontWeight: 800, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      👤 अर्जदार खाते माहिती (Profile Auto-filled)
                    </div>
                    <div style={{ fontSize: '1rem', fontWeight: 800, color: '#0c4a6e', marginTop: 2 }}>
                      {applicantName || user?.full_name} <span style={{ fontWeight: 600, fontSize: '0.9rem', color: '#0284c7' }}>({applicantPhone || user?.phone})</span>
                    </div>
                    {applicantAddress && (
                      <div style={{ fontSize: '0.84rem', color: '#334155', marginTop: 2 }}>
                        🏠 पत्ता: {applicantAddress}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setShowEditApplicant(prev => !prev)}
                    style={{
                      background: '#0284c7', color: '#fff', border: 'none', borderRadius: 8,
                      padding: '8px 14px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                    }}
                  >
                    {showEditApplicant ? '✓ पूर्ण झाले' : '✏️ नाव / पत्ता संपादन करा'}
                  </button>
                </div>

                {/* Edit Applicant Form if toggled */}
                {showEditApplicant && (
                  <div style={{ background: '#f8fafc', borderRadius: 10, padding: '16px 16px 2px', marginBottom: 18, border: '1px solid #cbd5e1' }}>
                    <p style={{ margin: '0 0 12px', fontWeight: 700, color: '#334155', fontSize: '0.8rem', textTransform: 'uppercase' }}>अर्जदाराची प्राथमिक माहिती</p>
                    <div className="form-two-col">
                      <Field label="अर्जदाराचे नाव *" value={applicantName} onChange={setApplicantName} required error={fieldErrors.applicantName} />
                      <Field label="फोन *" value={applicantPhone} onChange={v => setApplicantPhone(normalizePhoneInput(v))} type="tel" inputMode="numeric" maxLength={10} required error={fieldErrors.applicantPhone} />
                    </div>
                    <Field label="पत्ता" value={applicantAddress} onChange={setApplicantAddress} />
                    <Field label="उद्देश" value={purpose} onChange={setPurpose} placeholder="उदा. शाळा प्रवेश, पासपोर्ट, अधिकृत नोंद" />
                  </div>
                )}

                {/* ── Dynamic Section-Wise Form Rendering ── */}
                {dynamicFormSections.length === 0 && (
                  <div style={{ background: '#f8fafc', padding: '24px 20px', borderRadius: 12, border: '1.5px dashed #cbd5e1', textAlign: 'center', color: '#64748b', margin: '20px 0' }}>
                    <div style={{ fontSize: '1.8rem', marginBottom: 6 }}>📝</div>
                    <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#334155' }}>या प्रमाणपत्रासाठी इनपुट क्षेत्रे कॉन्फिगर केलेली नाहीत.</div>
                    <div style={{ fontSize: '0.82rem', marginTop: 4, color: '#64748b' }}>कृपया ग्रामपंचायत प्रशासकाद्वारे ॲडमिन पॅनेलमध्ये इनपुट क्षेत्रे (Form Fields) जोडल्यानंतर येथे फॉर्म दिसेल.</div>
                  </div>
                )}
                {dynamicFormSections.map((sec, secIdx) => (
                  <div
                    key={sec.section_id || secIdx}
                    className={'citizen-certificate-section' + (secIdx % 2 === 0 ? '' : ' citizen-certificate-section--alternate')}
                  >
                    <div className="citizen-certificate-section__header">
                      <span className="citizen-certificate-section__icon" aria-hidden="true">{sec.section_icon || '📌'}</span>
                      <span>{sec.section_title}</span>
                    </div>
                    <div className="form-two-col citizen-certificate-section__body">
                      {(sec.fields || []).map(field => {
                        if (!isCertificateFieldVisible(field, resolvedTypeFields)) return null;
                        const fieldKey = field.key;
                        const fieldValue = resolvedTypeFields[fieldKey] ?? '';
                        const fieldError = fieldErrors[fieldKey];

                        return (
                          <div key={fieldKey} style={{ gridColumn: (field.type === 'textarea' || field.is_bilingual) ? '1 / -1' : undefined }}>
                            <Field
                              label={field.label}
                              type={field.type}
                              isBilingual={Boolean(field.is_bilingual)}
                              options={field.options}
                              required={isCertificateFieldRequired(field, resolvedTypeFields)}
                              placeholder={field.placeholder}
                              value={fieldValue}
                              onChange={v => {
                                if (field.readonly || field.type === 'calculated') return;
                                const nextVal = fieldKey.includes('aadhaar')
                                  ? toEnglishDigits(v).replace(/\D/g, '').slice(0, 12)
                                  : v;
                                setTypeFields(prev => ({ ...prev, [fieldKey]: nextVal }));
                                if (fieldErrors[fieldKey]) setFieldErrors(prev => ({ ...prev, [fieldKey]: '' }));
                              }}
                              inputMode={field.inputMode}
                              minLength={field.minLength}
                              maxLength={field.maxLength}
                              min={field.min}
                              max={field.max}
                              error={fieldError}
                              readOnly={field.readonly}
                              helperText={field.type === 'calculated' && field.calculation && field.calculation.as_of_field && !resolvedTypeFields[field.calculation.as_of_field]
                                ? ('हे मूल्य मोजण्यासाठी प्रथम ' + ((configuredCustomFields.find(item => item.key === field.calculation.as_of_field) && configuredCustomFields.find(item => item.key === field.calculation.as_of_field).label) || field.calculation.as_of_field) + ' निवडा.')
                                : (field.type === 'calculated' ? 'हे मूल्य संबंधित तारखांवरून आपोआप मोजले जाते.' : '')}
                            />

                            {/* Groom Age Eligibility Indicator */}
                            {fieldKey === 'groom_dob' && typeFields.groom_dob && typeFields.marriage_date && (() => {
                              const age = calculateAgeOnDate(typeFields.groom_dob, typeFields.marriage_date);
                              if (age === null) return null;
                              const isEligible = age >= 21;
                              return (
                                <div style={{
                                  marginTop: -8, marginBottom: 12, padding: '6px 12px', borderRadius: 6,
                                  background: isEligible ? '#f0fdf4' : '#fef2f2',
                                  border: '1px solid ' + (isEligible ? '#86efac' : '#fca5a5'),
                                  color: isEligible ? '#166534' : '#991b1b', fontSize: '0.82rem', fontWeight: 700
                                }}>
                                  {isEligible
                                    ? ('✅ विवाह वेळी वय: ' + toMarathiDigits(age) + ' वर्षे (वय पात्रता पूर्ण)')
                                    : ('⚠️ वराचे वय विवाह वेळी किमान २१ वर्षे असणे आवश्यक आहे! (सध्याचे वय: ' + toMarathiDigits(age) + ' वर्षे)')
                                  }
                                </div>
                              );
                            })()}

                            {/* Bride Age Eligibility Indicator */}
                            {fieldKey === 'bride_dob' && typeFields.bride_dob && typeFields.marriage_date && (() => {
                              const age = calculateAgeOnDate(typeFields.bride_dob, typeFields.marriage_date);
                              if (age === null) return null;
                              const isEligible = age >= 18;
                              return (
                                <div style={{
                                  marginTop: -8, marginBottom: 12, padding: '6px 12px', borderRadius: 6,
                                  background: isEligible ? '#f0fdf4' : '#fef2f2',
                                  border: '1px solid ' + (isEligible ? '#86efac' : '#fca5a5'),
                                  color: isEligible ? '#166534' : '#991b1b', fontSize: '0.82rem', fontWeight: 700
                                }}>
                                  {isEligible
                                    ? ('✅ विवाह वेळी वय: ' + toMarathiDigits(age) + ' वर्षे (वय पात्रता पूर्ण)')
                                    : ('⚠️ वधूचे वय विवाह वेळी किमान १८ वर्षे असणे आवश्यक आहे! (सध्याचे वय: ' + toMarathiDigits(age) + ' वर्षे)')
                                  }
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ── STEP 3: Document Upload ──────────────────────────────── */}
            {step === 3 && (
              <div className="apply-page-card-padding" style={{ padding: '28px 28px 20px' }}>
                <h3 style={{ margin: '0 0 4px', color: '#1a5276', fontSize: '1.1rem' }}>📎 कागदपत्रे अपलोड करा</h3>
                <p style={{ margin: '0 0 18px', color: '#9ca3af', fontSize: '0.82rem' }}>JPG, PNG, PDF, WebP स्वीकारले जातात (प्रत्येक 5MB पर्यंत)</p>
                
                {/* Printable Offline Form Download Alert inside Step 3 */}
                {selectedFee?.offline_form_url && (
                  <div style={{
                    marginBottom: 18, padding: '14px 18px', background: '#f0fdf4', borderRadius: 10,
                    border: '1.5px solid #86efac', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12
                  }}>
                    <div>
                      <div style={{ fontWeight: 800, color: '#166534', fontSize: '0.92rem' }}>
                        📥 ऑफलाईन अर्ज भरून जोडणे आवश्यक आहे का?
                      </div>
                      <div style={{ fontSize: '0.82rem', color: '#15803d', marginTop: 2 }}>
                        तुम्ही अधिकृत ब्लँक ऑफलाईन फॉर्म डाऊनलोड करून, स्वहस्ते भरून व स्वाक्षरी करून येथे अपलोड करू शकता.
                      </div>
                    </div>
                    <a
                      href={selectedFee.offline_form_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        padding: '9px 18px', background: '#16a34a', color: '#fff', borderRadius: 8,
                        fontWeight: 700, fontSize: '0.85rem', textDecoration: 'none', boxShadow: '0 2px 6px rgba(22,163,74,0.3)'
                      }}
                    >
                      📥 ऑफलाईन फॉर्म PDF डाउनलोड
                    </a>
                  </div>
                )}

                {fieldErrors.documents && (
                  <div style={{ marginBottom: 14, padding: '10px 12px', border: '1px solid #fca5a5', borderRadius: 8, background: '#fef2f2', color: '#991b1b', fontWeight: 700, fontSize: '0.84rem' }}>
                    {fieldErrors.documents}
                  </div>
                )}

                {/* Document Checklist */}
                {currentDocuments.length > 0 && (
                  <div style={{ marginBottom: 18 }}>
                    <p style={{ margin: '0 0 10px', fontWeight: 700, color: '#6b7280', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>आवश्यक कागदपत्रे</p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {currentDocuments.map((docRequirement, i) => {
                        const docName = docRequirement.name;
                        const uploadedDoc = documents[docName];
                        const isUploaded = uploadedDoc?.url;
                        const isUploading = uploadedDoc?.uploading;
                        const hasError = uploadedDoc?.error;

                        return (
                          <div key={i} className="apply-document-row" style={{
                            display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
                            background: isUploaded ? '#f0fdf4' : hasError ? '#fef2f2' : '#fafafa',
                            border: isUploaded ? '1.5px solid #86efac' : hasError ? '1.5px solid #fca5a5' : '1.5px dashed #d1d5db',
                            borderRadius: 10, transition: 'all 0.2s', flexWrap: 'wrap'
                          }}>
                            <div style={{ flex: 1, minWidth: 200 }}>
                              <p style={{ margin: 0, fontWeight: 600, color: '#374151', fontSize: '0.9rem' }}>
                                {docName}
                                <span style={{
                                  marginLeft: 8,
                                  padding: '2px 7px',
                                  borderRadius: 999,
                                  background: docRequirement.is_required === false ? '#e0f2fe' : '#fee2e2',
                                  color: docRequirement.is_required === false ? '#075985' : '#991b1b',
                                  fontSize: '0.7rem',
                                  fontWeight: 800,
                                }}>
                                  {docRequirement.is_required === false ? 'Optional' : 'Required'}
                                </span>
                              </p>
                              {isUploaded && (
                                <p style={{ margin: '2px 0 0', color: '#16a34a', fontSize: '0.78rem', fontWeight: 600 }}>
                                  ✓ अपलोड झाले: {uploadedDoc.originalName}
                                </p>
                              )}
                              {hasError && (
                                <p style={{ margin: '2px 0 0', color: '#dc2626', fontSize: '0.78rem', fontWeight: 600 }}>
                                  ✕ {uploadedDoc.errorMsg || 'अपलोड अयशस्वी'}
                                </p>
                              )}
                            </div>

                            <input
                              ref={el => (fileInputRefs.current[docName] = el)}
                              type="file"
                              accept="image/*,.pdf"
                              style={{ display: 'none' }}
                              onChange={e => handleDocUpload(docName, e.target.files[0])}
                            />

                            {isUploaded ? (
                              <div className="apply-document-actions" style={{ display: 'flex', gap: 6 }}>
                                {uploadedDoc.mimeType?.startsWith('image/') && (
                                  <a
                                    href={uploadedDoc.previewUrl || documentUrl(uploadedDoc.url)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    aria-label={docName + ' पहा'}
                                    style={{ padding: '6px 12px', background: '#e0f2fe', border: '1px solid #7dd3fc', borderRadius: 6, color: '#075985', fontSize: '0.8rem', textDecoration: 'none', cursor: 'pointer', fontWeight: 700 }}
                                  >
                                    👁 पहा
                                  </a>
                                )}
                                <button type="button" onClick={() => downloadDocument(uploadedDoc.previewUrl || documentUrl(uploadedDoc.url), uploadedDoc.originalName || docName)}
                                  style={{ padding: '6px 12px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 6, color: '#166534', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 700 }}>
                                  डाउनलोड ↓
                                </button>
                                <button
                                  type="button"
                                  onClick={() => fileInputRefs.current[docName]?.click()}
                                  style={{ padding: '6px 12px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}
                                >
                                  बदला
                                </button>
                                <button
                                  type="button"
                                  onClick={() => removeDoc(docName)}
                                  style={{ padding: '6px 10px', background: '#fee2e2', border: 'none', borderRadius: 6, color: '#991b1b', fontSize: '0.8rem', cursor: 'pointer', fontWeight: 600 }}
                                >
                                  हटवा
                                </button>
                              </div>
                            ) : (
                              <button
                                type="button"
                                disabled={isUploading}
                                onClick={() => fileInputRefs.current[docName]?.click()}
                                style={{
                                  padding: '8px 16px',
                                  background: isUploading ? '#9ca3af' : '#1a5276',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: 8,
                                  fontSize: '0.82rem',
                                  fontWeight: 700,
                                  cursor: isUploading ? 'not-allowed' : 'pointer',
                                }}
                              >
                                {isUploading ? 'अपलोड होत आहे...' : '⬆️ अपलोड'}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Custom document upload section */}
                <div style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid #e5e7eb' }}>
                  <p style={{ margin: '0 0 10px', fontWeight: 700, color: '#6b7280', fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    इतर अतिरिक्त कागदपत्रे (ऐच्छिक)
                  </p>
                  <div className="apply-custom-document-input" style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                    <input
                      type="text"
                      placeholder="उदा. लाईट बिल, कर पावती"
                      value={newDocName}
                      onChange={e => setNewDocName(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCustomDoc(); } }}
                      style={{ flex: 1, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 8, fontSize: '0.88rem' }}
                    />
                    <button
                      type="button"
                      onClick={addCustomDoc}
                      disabled={!newDocName.trim() || currentDocuments.length + customDocs.length >= MAX_CERTIFICATE_DOCUMENTS}
                      style={{
                        padding: '8px 16px', background: newDocName.trim() ? '#1a5276' : '#e5e7eb',
                        color: newDocName.trim() ? '#fff' : '#9ca3af', border: 'none', borderRadius: 8,
                        fontWeight: 700, fontSize: '0.82rem', cursor: newDocName.trim() ? 'pointer' : 'not-allowed',
                      }}
                    >
                      + जोडा
                    </button>
                  </div>

                  {customDocs.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {customDocs.map(doc => (
                        <div key={doc.id} className="apply-custom-document-row" style={{
                          display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
                          background: doc.url ? '#f0fdf4' : doc.error ? '#fef2f2' : '#fafafa',
                          border: doc.url ? '1px solid #86efac' : doc.error ? '1px solid #fca5a5' : '1px dashed #d1d5db',
                          borderRadius: 8, flexWrap: 'wrap'
                        }}>
                          <div style={{ flex: 1, minWidth: 180 }}>
                            <span style={{ fontWeight: 600, fontSize: '0.88rem', color: '#374151' }}>{doc.name}</span>
                            {doc.url && <span style={{ marginLeft: 8, color: '#16a34a', fontSize: '0.76rem', fontWeight: 600 }}>✓ {doc.originalName}</span>}
                            {doc.error && <span style={{ marginLeft: 8, color: '#dc2626', fontSize: '0.76rem', fontWeight: 600 }}>✕ {doc.errorMsg}</span>}
                          </div>

                          <input
                            ref={el => (fileInputRefs.current[doc.id] = el)}
                            type="file" accept="image/*,.pdf" style={{ display: 'none' }}
                            onChange={e => handleCustomDocUpload(doc.id, e.target.files[0])}
                          />

                          {doc.url ? (
                            <div style={{ display: 'flex', gap: 6 }}>
                              {doc.mimeType?.startsWith('image/') && <a href={doc.previewUrl || documentUrl(doc.url)} target="_blank" rel="noopener noreferrer" style={{ padding: '4px 8px', background: '#e0f2fe', border: '1px solid #7dd3fc', borderRadius: 4, color: '#075985', fontSize: '0.75rem', textDecoration: 'none', fontWeight: 700 }}>👁 पहा</a>}
                              <button type="button" onClick={() => downloadDocument(doc.previewUrl || documentUrl(doc.url), doc.originalName || doc.name)} style={{ padding: '4px 8px', background: '#dcfce7', border: '1px solid #86efac', borderRadius: 4, color: '#166534', fontSize: '0.75rem', cursor: 'pointer', fontWeight: 700 }}>डाउनलोड ↓</button>
                              <button type="button" onClick={() => fileInputRefs.current[doc.id]?.click()} style={{ padding: '4px 8px', background: '#fff', border: '1px solid #d1d5db', borderRadius: 4, color: '#374151', fontSize: '0.75rem', cursor: 'pointer' }}>बदला</button>
                              <button type="button" onClick={() => removeCustomDoc(doc.id)} style={{ padding: '4px 8px', background: '#fee2e2', border: 'none', borderRadius: 4, color: '#991b1b', fontSize: '0.75rem', cursor: 'pointer' }}>हटवा</button>
                            </div>
                          ) : (
                            <button type="button" disabled={doc.uploading} onClick={() => fileInputRefs.current[doc.id]?.click()}
                              style={{ padding: '6px 12px', background: doc.uploading ? '#9ca3af' : '#1a5276', color: '#fff', border: 'none', borderRadius: 6, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer' }}>
                              {doc.uploading ? '...' : 'अपलोड'}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── STEP 4: Review ───────────────────────────────────────── */}
            {step === 4 && (
              <div className="apply-page-card-padding" style={{ padding: '28px 28px 20px' }}>
                <h3 style={{ margin: '0 0 4px', color: '#1a5276', fontSize: '1.1rem' }}>📋 माहिती तपासा व सादर करा</h3>
                <p style={{ margin: '0 0 18px', color: '#6b7280', fontSize: '0.86rem' }}>कृपया अर्ज सादर करण्यापूर्वी सर्व माहितीची खात्री करा.</p>

                <div style={{ background: '#f9fafb', borderRadius: 10, padding: 18, marginBottom: 16, border: '1px solid #e5e7eb' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #e5e7eb', paddingBottom: 10, marginBottom: 12 }}>
                    <span style={{ fontWeight: 700, color: '#1a5276' }}>प्रमाणपत्र प्रकार:</span>
                    <span style={{ fontWeight: 700, color: '#374151' }}>{currentCert.icon} {currentCert.label}</span>
                  </div>

                  <div className="form-two-col" style={{ fontSize: '0.88rem' }}>
                    <div><span style={{ color: '#6b7280' }}>अर्जदार:</span> <strong>{applicantName}</strong></div>
                    <div><span style={{ color: '#6b7280' }}>फोन:</span> <strong>{applicantPhone}</strong></div>
                    {applicantAddress && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#6b7280' }}>पत्ता:</span> {applicantAddress}</div>}
                    {purpose && <div style={{ gridColumn: '1 / -1' }}><span style={{ color: '#6b7280' }}>उद्देश:</span> {purpose}</div>}
                  </div>

                  {Object.keys(resolvedTypeFields).length > 0 && (
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed #d1d5db' }}>
                      <p style={{ margin: '0 0 8px', fontWeight: 700, color: '#1a5276', fontSize: '0.84rem' }}>तपशील माहिती:</p>
                      <div className="form-two-col" style={{ fontSize: '0.86rem' }}>
                        {Object.entries(resolvedTypeFields).map(([k, v]) => {
                          const f = configuredCustomFields.find(field => field.key === k);
                          if (v === undefined || v === null || v === '' || !isCertificateFieldVisible(f || {}, resolvedTypeFields)) return null;
                          return (
                            <div key={k} style={f?.type === 'textarea' ? { gridColumn: '1 / -1' } : {}}>
                              <span style={{ color: '#6b7280' }}>{stripRequiredMark(f?.label || k)}:</span> <strong>{String(v)}</strong>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Dynamic Custom Fields Summary */}
                  {Object.keys(customFieldsData).length > 0 && (
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed #d1d5db' }}>
                      <p style={{ margin: '0 0 8px', fontWeight: 700, color: '#1a5276', fontSize: '0.84rem' }}>ग्रामपंचायत अतिरिक्त तपशील:</p>
                      <div className="form-two-col" style={{ fontSize: '0.86rem' }}>
                        {Object.entries(customFieldsData).map(([k, v]) => {
                          const f = configuredCustomFields.find(field => field.key === k);
                          if (!v) return null;
                          return (
                            <div key={k}>
                              <span style={{ color: '#6b7280' }}>{f?.label || k}:</span> <strong>{String(v)}</strong>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Uploaded docs list summary */}
                  {(Object.keys(documents).some(k => documents[k]?.url) || customDocs.some(d => d.url)) && (
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px dashed #d1d5db' }}>
                      <p style={{ margin: '0 0 6px', fontWeight: 700, color: '#1a5276', fontSize: '0.84rem' }}>जोडलेली कागदपत्रे:</p>
                      <DocumentPreviewTable documents={[
                        ...Object.entries(documents).filter(([, value]) => value.url).map(([name, value]) => ({
                          key: name, name, fileName: value.originalName || name, url: value.previewUrl || documentUrl(value.url),
                        })),
                        ...customDocs.filter(document => document.url).map(document => ({
                          key: document.id, name: document.name, fileName: document.originalName || document.name, url: document.previewUrl || documentUrl(document.url),
                        })),
                      ]} />
                    </div>
                  )}
                </div>


                {/* Fee and Payment Section */}
                {(() => {
                  const feeAmount = Number(selectedFee?.fee_amount || 0);
                  if (feeAmount <= 0) {
                    return (
                      <div style={{ padding: '14px 18px', background: '#f0fdf4', borderRadius: 10, border: '1.5px solid #86efac', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <div>
                          <strong style={{ color: '#166534', fontSize: '0.95rem' }}>दाखला अर्ज शुल्क: मोफत (₹0)</strong>
                          <div style={{ fontSize: '0.82rem', color: '#15803d', marginTop: 2 }}>या प्रमाणपत्रासाठी कोणतेही शासकीय शुल्क नाही.</div>
                        </div>
                        <span style={{ fontSize: '1.5rem' }}>🎁</span>
                      </div>
                    );
                  }

                  if (paymentAvailability.available) {
                    return (
                      <div style={{ background: '#f8fafc', borderRadius: 12, padding: 18, marginBottom: 16, border: '1.5px solid #0284c7' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
                          <div>
                            <span style={{ fontSize: '0.78rem', fontWeight: 800, color: '#0369a1', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                              💳 शासकीय शुल्क व भरणा पर्याय
                            </span>
                            <div style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0c4a6e', marginTop: 2 }}>
                              शुल्क रक्कम: ₹{feeAmount.toFixed(2)}
                            </div>
                          </div>
                          <span style={{ background: '#e0f2fe', color: '#0369a1', padding: '4px 12px', borderRadius: 20, fontSize: '0.8rem', fontWeight: 700 }}>
                            ✓ सुरक्षित ऑनलाइन भरणा उपलब्ध
                          </span>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                          <label style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
                            background: paymentChoice === 'online' ? '#eff6ff' : '#fff',
                            border: `1.5px solid ${paymentChoice === 'online' ? '#3b82f6' : '#cbd5e1'}`,
                            borderRadius: 8, cursor: 'pointer', transition: 'all 0.2s',
                          }}>
                            <input
                              type="radio"
                              name="payment_choice"
                              value="online"
                              checked={paymentChoice === 'online'}
                              onChange={() => setPaymentChoice('online')}
                              style={{ marginTop: 3 }}
                            />
                            <div>
                              <strong style={{ color: '#1e3a8a', fontSize: '0.92rem' }}>ऑनलाइन त्वरित भरणा (Razorpay UPI, QR, कार्ड, नेटबँकिंग)</strong>
                              <p style={{ margin: '2px 0 0', color: '#475569', fontSize: '0.82rem' }}>
                                अर्ज सादर करताना सुरक्षित पेमेंट गेटवेद्वारे त्वरित शुल्क भरा आणि अधिकृत पावती मिळवा.
                              </p>
                            </div>
                          </label>

                          <label style={{
                            display: 'flex', alignItems: 'flex-start', gap: 10, padding: '12px 14px',
                            background: paymentChoice === 'cash' ? '#eff6ff' : '#fff',
                            border: `1.5px solid ${paymentChoice === 'cash' ? '#3b82f6' : '#cbd5e1'}`,
                            borderRadius: 8, cursor: 'pointer', transition: 'all 0.2s',
                          }}>
                            <input
                              type="radio"
                              name="payment_choice"
                              value="cash"
                              checked={paymentChoice === 'cash'}
                              onChange={() => setPaymentChoice('cash')}
                              style={{ marginTop: 3 }}
                            />
                            <div>
                              <strong style={{ color: '#334155', fontSize: '0.92rem' }}>कार्यालयात रोख भरणा करा (Pay Later at GP Office)</strong>
                              <p style={{ margin: '2px 0 0', color: '#64748b', fontSize: '0.82rem' }}>
                                अर्ज आताच सबमिट करा; दाखला शुल्क नंतर ग्रामपंचायत कार्यालयात रोख जमा करू शकता किंवा डॅशबोर्डवरून ऑनलाइन भरू शकता.
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div style={{ background: '#fefce8', borderRadius: 12, padding: 16, marginBottom: 16, border: '1.5px solid #facc15' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <strong style={{ color: '#854d0e', fontSize: '0.95rem' }}>शासकीय दाखला शुल्क: ₹{feeAmount.toFixed(2)}</strong>
                          <div style={{ fontSize: '0.82rem', color: '#713f12', marginTop: 2 }}>
                            या ग्रामपंचायतीसाठी ऑनलाइन भरणा सुविधा सध्या सुरू नाही. अर्ज सबमिट केल्यानंतर शुल्क ग्रामपंचायत कार्यालयात रोख जमा करावे.
                          </div>
                        </div>
                        <span style={{ fontSize: '1.5rem' }}>🏢</span>
                      </div>
                    </div>
                  );
                })()}

                <div style={{ padding: '12px 16px', background: '#fff7ed', borderRadius: 10, border: '1px solid #fed7aa', fontSize: '0.84rem', color: '#9a3412', lineHeight: 1.6 }}>
                  ℹ️ अर्ज सबमिट केल्यानंतर ग्रामपंचायत कर्मचारी आपल्या कागदपत्रांची पडताळणी करतील.
                </div>
              </div>
            )}

            {/* ── Navigation Buttons ───────────────────────────────────── */}
            <div className="apply-page-footer" style={{ display: 'flex', justifyContent: 'space-between', padding: '16px 28px 24px', background: '#fafafa', borderTop: '1px solid #f0f0f0' }}>
              {step > 1 ? (
                <button type="button" onClick={() => setStep(s => s - 1)} disabled={submitting}
                  style={{ padding: '10px 22px', background: '#fff', border: '1.5px solid #d1d5db', borderRadius: 8, color: '#374151', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer' }}>
                  ← मागे
                </button>
              ) : <div />}

              {step < 4 ? (
                <button type="button" onClick={goNext} disabled={!taxEligibility.eligible}
                  style={{
                    padding: '10px 26px',
                    background: !taxEligibility.eligible ? '#9ca3af' : '#e67e22',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: '0.9rem',
                    cursor: !taxEligibility.eligible ? 'not-allowed' : 'pointer',
                    boxShadow: !taxEligibility.eligible ? 'none' : '0 2px 8px rgba(230,126,34,0.3)',
                  }}
                  title={!taxEligibility.eligible ? 'थकीत कर भरल्यानंतरच पुढे जाता येईल' : ''}
                >
                  पुढे जा →
                </button>
              ) : (
                <button type="button" onClick={handleSubmit} disabled={submitting || !taxEligibility.eligible}
                  style={{
                    padding: '11px 30px',
                    background: (submitting || !taxEligibility.eligible) ? '#9ca3af' : '#16a34a',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    cursor: (submitting || !taxEligibility.eligible) ? 'not-allowed' : 'pointer',
                    boxShadow: (submitting || !taxEligibility.eligible) ? 'none' : '0 2px 10px rgba(22,163,74,0.3)',
                  }}
                  title={!taxEligibility.eligible ? 'थकीत कर भरल्यानंतरच अर्ज सादर करता येईल' : ''}
                >
                  {submitting
                    ? 'प्रक्रिया सुरू आहे...'
                    : (Number(selectedFee?.fee_amount || 0) > 0 && paymentAvailability.available && paymentChoice === 'online')
                      ? `💳 ₹${Number(selectedFee?.fee_amount || 0).toFixed(2)} भरा व अर्ज सादर करा`
                      : '✓ अर्ज सादर करा'}
                </button>
              )}
            </div>

          </div>
          </>
        )}
        </div>

        {/* Right Guidance & Helpline Sidebar */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          
          {/* Card 1: Certificate-Specific Dynamic Guidance */}
          <div style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.94rem', fontWeight: 800, color: '#1a237e', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>📌</span> {currentCert.label} — मार्गदर्शक सूचना
            </div>

            {/* Offline Form Download Button if configured */}
            {selectedFee?.offline_form_url && (
              <div style={{ marginBottom: 14, padding: '12px 14px', background: '#f0fdf4', borderRadius: 10, border: '1px solid #86efac' }}>
                <div style={{ fontSize: '0.82rem', fontWeight: 800, color: '#166534', marginBottom: 4 }}>
                  📄 ब्लँक ऑफलाईन फॉर्म उपलब्ध!
                </div>
                <a
                  href={selectedFee.offline_form_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    display: 'inline-block', width: '100%', boxSizing: 'border-box', textAlign: 'center',
                    padding: '8px 12px', background: '#16a34a', color: '#fff', borderRadius: 6,
                    fontWeight: 700, fontSize: '0.82rem', textDecoration: 'none'
                  }}
                >
                  📥 ऑफलाईन फॉर्म PDF डाउनलोड करा
                </a>
              </div>
            )}

            <ul style={{ margin: 0, paddingLeft: 18, fontSize: '0.84rem', color: '#334155', lineHeight: 1.6 }}>
              {certGuidelines.map((g, idx) => (
                <li key={idx} style={{ marginBottom: 8 }}>
                  <strong>{g.title}:</strong> {g.text}
                </li>
              ))}
            </ul>
          </div>

          {/* Card 2: Fee & Processing Summary */}
          <div style={{ background: '#fff', borderRadius: 14, padding: 20, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid #e2e8f0' }}>
            <div style={{ fontSize: '0.94rem', fontWeight: 800, color: '#1a237e', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>📊</span> शुल्क व प्रक्रिया माहिती
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, fontSize: '0.86rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: 6 }}>
                <span style={{ color: '#64748b' }}>सेवा प्रकार:</span>
                <strong style={{ color: '#0f172a' }}>{currentCert.label}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: 6 }}>
                <span style={{ color: '#64748b' }}>शासकीय शुल्क:</span>
                <strong style={{ color: '#16a34a' }}>{selectedFee ? (selectedFee.contact_gp_for_fees ? 'शुल्क माहितीसाठी ग्रामपंचायतीशी संपर्क साधा' : (Number(selectedFee.fee_amount || 0) === 0 ? 'मोफत' : ('₹ ' + formatMarathiNumber(selectedFee.fee_amount || 0)))) : 'ग्रामपंचायत नियमानुसार'}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f1f5f9', paddingBottom: 6 }}>
                <span style={{ color: '#64748b' }}>अंदाजित वेळ:</span>
                <strong style={{ color: '#e67e22' }}>{toMarathiDigits(currentProcessingTime)}</strong>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#64748b' }}>पडताळणी अधिकारी:</span>
                <strong style={{ color: '#1a237e' }}>ग्रामसेवक / VDO</strong>
              </div>
            </div>
          </div>

          {/* Card 3: Tax Status Indicator */}
          <div style={{
            background: !taxEligibility.eligible ? '#fffbeb' : '#f0fdf4',
            borderRadius: 14,
            padding: 18,
            boxShadow: '0 2px 14px rgba(0,0,0,0.05)',
            border: '1.5px solid ' + (!taxEligibility.eligible ? '#f59e0b' : '#86efac'),
          }}>
            <div style={{
              fontSize: '0.92rem',
              fontWeight: 800,
              color: !taxEligibility.eligible ? '#92400e' : '#166534',
              marginBottom: 8,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              <span>{!taxEligibility.eligible ? '⚠️' : '✅'}</span>
              <span>कर स्थिती (Tax Status)</span>
            </div>
            {!taxEligibility.eligible ? (
              <div style={{ fontSize: '0.85rem', color: '#78350f', lineHeight: 1.55 }}>
                <p style={{ margin: '0 0 8px', fontWeight: 700 }}>
                  थकीत कर: <span style={{ color: '#dc2626', fontSize: '1rem' }}>₹{taxEligibility.total_pending_amount}</span>
                </p>
                <p style={{ margin: '0 0 10px', fontSize: '0.82rem' }}>
                  प्रमाणपत्र अर्जासाठी थकीत कर भरणे बंधनकारक आहे.
                </p>
                <Link
                  to={taxEligibility.pay_url || ('/gp/' + tenantSlug + '/my-taxes')}
                  style={{
                    display: 'block',
                    textAlign: 'center',
                    background: '#047857',
                    color: '#fff',
                    padding: '8px 12px',
                    borderRadius: 6,
                    fontWeight: 700,
                    fontSize: '0.82rem',
                    textDecoration: 'none',
                  }}
                >
                  💳 कर भरा / Pay Tax
                </Link>
              </div>
            ) : (
              <div style={{ fontSize: '0.84rem', color: '#166534', lineHeight: 1.5 }}>
                सर्व कर नियमित भरलेले आहेत. आपण प्रमाणपत्रासाठी अर्ज करू शकता.
              </div>
            )}
          </div>

          {/* Card 4: Grampanchayat Helpline & Contact */}
          <div style={{ background: '#f8fafc', borderRadius: 14, padding: 20, border: '1.5px solid #cbd5e1' }}>
            <div style={{ fontSize: '0.9rem', fontWeight: 800, color: '#0f172a', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span>📞</span> ग्रामपंचायत मदत केंद्र
            </div>
            <div style={{ fontSize: '0.84rem', color: '#475569', lineHeight: 1.6 }}>
              अर्जाबाबत अडचण असल्यास किंवा चौकशीसाठी ग्रामपंचायत कार्यालयाशी संपर्क साधा:
              <div style={{ marginTop: 8, fontWeight: 700, color: '#1a237e' }}>
                🏢 {tenantData?.name || 'ग्रामपंचायत देऊळगाव गाडा'}<br />
                📍 ता. {tenantData?.taluka || 'दौंड'}, जि. {tenantData?.district || 'पुणे'}<br />
                ☎️ फोन: {tenantData?.phone || '+९१ ९९२२०५४२८५'}
              </div>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}
